import { TFunction } from 'i18next/typescript/t';
import { NonNullableOrderData } from './reusableTypes';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  intlAssetValueFormatting,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { getAssetTradeUrl } from '../../route-builders';
import { ReactNode } from 'react';
import { getStopOrderTranslations } from '../../orders/utils/getStopOrderTranslations';
import { OrderStatusLabels, OrderOperation } from '../../orders/types';
import { isBpfsAsset } from './isBpfsAsset';
import { STOP_ORDER_VALIDITY_MONTHS } from './constants';
import { AssetType } from '@bts-web/core-features/asset-details';

/**
 * Order data with type narrowed to OrderOperation (BUY | SELL).
 * Callers must ensure order.type is validated before passing to getOrderContentMessages.
 */
export type OrderDataWithValidType = Omit<NonNullableOrderData, 'operation'> & {
  operation: OrderOperation;
};

export type OrderMessage = {
  title: string;
  subtitle: ReactNode;
  poweredByText?: string;
  statusContent?: ReactNode;
  secondaryButton: {
    text: string;
    url: string;
  };
  primaryButton: {
    text: string;
    url: string;
  };
};

export const getOrderContentMessages = ({
  translationUtil,
  locale,
  transactionData,
  reason,
}: {
  translationUtil: TFunction;
  locale: string;
  transactionData: OrderDataWithValidType;
  reason?: string;
}): Record<keyof typeof OrderStatusLabels, OrderMessage> => {
  const { currency } = getAppConfig();

  const translations = getStopOrderTranslations(translationUtil);

  const {
    fiatAmount,
    assetAmount,
    targetPrice,
    asset,
    operation: orderType,
  } = transactionData;

  const description =
    orderType === OrderOperation.BUY
      ? `${translationUtil('stop_order_buy_confirmation_subtitle_1', {
          asset: asset?.symbol,
          amount: intlFiatValueFormatting(fiatAmount?.value || 0, {
            locale,
            notation: 'standard',
            currency,
            fractionDigits: fiatAmount?.precision,
          }),
          targetAmount: intlFiatValueFormatting(targetPrice?.value || 0, {
            locale,
            notation: 'standard',
            currency,
            fractionDigits: targetPrice?.precision,
          }),
        })} ${translationUtil('stop_order_buy_confirmation_subtitle_2', {
          asset: asset?.symbol,
          amount: STOP_ORDER_VALIDITY_MONTHS,
        })}`
      : translationUtil('stop_order_sell_confirmation_subtitle', {
          ticker: asset?.symbol,
          asset: asset?.symbol,
          amount: intlAssetValueFormatting(assetAmount?.value || 0, {
            locale,
            notation: 'standard',
            fractionDigits: assetAmount?.precision,
          }),
          targetAmount: intlFiatValueFormatting(targetPrice?.value || 0, {
            locale,
            notation: 'standard',
            currency,
            fractionDigits: targetPrice?.precision,
          }),
        });

  const poweredByText = isBpfsAsset(asset?.__typename as AssetType)
    ? translationUtil('powered_by_bpfs')
    : undefined;

  return {
    /**
     * The transaction status flow is: CREATED -> ACCEPTED -> FINISHED / FAILED / CANCELED
     * For the trade flow, only FINISHED / FAILED are possible end states
     */
    FINISHED: {
      title: translationUtil('stop_order_confirmation_title', {
        asset: asset?.name,
      }),
      subtitle: description,
      poweredByText,
      primaryButton: {
        text: translations.okGotIt,
        url: `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', asset?.id || '')}?ref=portfolio`,
      },
      secondaryButton: {
        text: translations.viewDetails,
        url: `${NEXT_ROUTES.ORDERS_DETAILS.replace(
          '[id]',
          transactionData.id,
        )}`,
      },
    },
    FAILED: {
      title:
        reason === 'insufficient_funds'
          ? translationUtil('insufficient.order.funds_title')
          : translations.orderRejectionTitle,
      subtitle:
        reason === 'insufficient_funds'
          ? translationUtil('insufficient.order.funds_description')
          : `${translations.orderRejectionDescription1}. ${translations.orderRejectionDescription2}`,
      poweredByText,
      primaryButton: {
        text: translations.tryAgain,
        url: getAssetTradeUrl(asset?.id || '', {
          type: 'orderForm',
          orderType: orderType,
        }),
      },
      secondaryButton: {
        text: translations.goToPortfolio,
        url: `${NEXT_ROUTES.PORTFOLIO}`,
      },
    },
  };
};
